import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Cookie, Settings, BarChart3, ToggleRight } from 'lucide-react';

function FadeSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function CookiePolicy() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const sections = [
    { icon: <Cookie size={22} />, title: '1. What Are Cookies', content: 'Cookies are small text files stored on your device when you visit our website. They help us understand how you interact with our site, remember your preferences, and improve your experience. Cookies may be \u201csession\u201d cookies (deleted when you close your browser) or \u201cpersistent\u201d cookies (remain on your device for a set period).' },
    { icon: <Settings size={22} />, title: '2. Types of Cookies We Use', content: 'Essential Cookies: Required for basic website functionality (session management, security). Analytics Cookies: Help us understand site usage patterns through anonymous data collection (e.g., pages visited, time spent). We use privacy-respecting analytics. Preference Cookies: Remember your settings and choices for a better experience.' },
    { icon: <BarChart3 size={22} />, title: '3. Third-Party Cookies', content: 'Our website may include embedded content from third parties such as Spotify (music players) and DigitalOcean (CDN for media assets). These services may set their own cookies. We have no control over third-party cookies, and their use is governed by the respective third party\u2019s privacy and cookie policies.' },
    { icon: <ToggleRight size={22} />, title: '4. Managing Cookies', content: 'You can control and manage cookies through your browser settings. Most browsers allow you to block or delete cookies. However, disabling cookies may affect the functionality of our website. For more information on managing cookies, visit your browser\u2019s help documentation.' },
  ];

  return (
    <div className="pp-page">
      <motion.div className="pp-hero" ref={heroRef}>
        <motion.div className="pp-hero-inner" style={{ y: heroY }}>
          <motion.div className="pp-hero-icon"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: 'spring', stiffness: 200 }}
          ><Cookie size={48} /></motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>Cookie Policy</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>Last updated: March 2025</motion.p>
        </motion.div>
      </motion.div>

      <div className="pp-content">
        {sections.map((s, i) => (
          <FadeSection key={i} delay={i * 0.08}>
            <div className="pp-card">
              <div className="pp-card-icon">{s.icon}</div>
              <h2>{s.title}</h2>
              <p>{s.content}</p>
            </div>
          </FadeSection>
        ))}

        <FadeSection delay={0.4}>
          <div className="pp-card">
            <div className="pp-card-icon"><Cookie size={22} /></div>
            <h2>5. Contact</h2>
            <p>For questions about our cookie practices, contact us at info@cloudfimusic.com. CloudfiMusic / Xu\u00e2n Di\u1ec7u Media Co., Ltd. Company ID: 0402222404.</p>
          </div>
        </FadeSection>
      </div>

      <div className="pp-footer-nav">
        <Link to="/terms-of-service">&larr; Terms of Service</Link>
        <Link to="/">Back to Home</Link>
      </div>
    </div>
  );
}
